import ApiWithLogging from '../utils/apiWithLogging';

// ข้อมูลผู้ใช้แบบย่อ
export interface UserInfo {
  id: number;
  username: string;
  full_name?: string;
}

// Interface สำหรับ Supplier
export interface Supplier {
  id: number;
  code: string;
  name: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  address?: string;
  tax_id?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  created_by_id?: number;
  created_by?: UserInfo;
  updated_by_id?: number;
  updated_by?: UserInfo;
}

// Interface สำหรับ Supplier Form
export interface SupplierForm {
  code: string;
  name: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  address?: string;
  tax_id?: string;
  notes?: string;
  is_active?: boolean;
}

// Interface สำหรับ Supplier List Response
export interface SupplierListResponse {
  items: Supplier[];
  total: number;
  page: number;
  limit: number;
}

// Interface สำหรับ Supplier Query Parameters
export interface GetSuppliersParams {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
}

const supplierService = {
  // ดึงรายการผู้จำหน่ายทั้งหมด
  async getSuppliers(params?: GetSuppliersParams): Promise<SupplierListResponse> {
    try {
      const response = await ApiWithLogging.get('/suppliers/',
        { params },
        {
          service: 'supplierService',
          action: 'view_suppliers_list',
          entityType: 'supplier'
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error;
    }
  },

  // ดึงรายการผู้จำหน่ายที่ใช้งานอยู่
  async getActiveSuppliers(): Promise<Supplier[]> {
    try {
      const response = await ApiWithLogging.get('/suppliers/active', {},
        {
          service: 'supplierService',
          action: 'view_active_suppliers',
          entityType: 'supplier'
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching active suppliers:', error);
      throw error;
    }
  },

  // ดึงข้อมูลผู้จำหน่ายตาม ID
  async getSupplierById(id: number): Promise<Supplier> {
    try {
      const response = await ApiWithLogging.get(`/suppliers/${id}`, {},
        {
          service: 'supplierService',
          action: 'view_supplier_detail',
          entityType: 'supplier',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching supplier by ID:', error);
      throw error;
    }
  },

  // สร้างผู้จำหน่ายใหม่
  async createSupplier(supplierData: SupplierForm): Promise<Supplier> {
    try {
      const response = await ApiWithLogging.post('/suppliers/', supplierData, {},
        {
          service: 'supplierService',
          action: 'create_supplier',
          entityType: 'supplier',
          details: { code: supplierData.code, name: supplierData.name }
        }
      );
      return response;
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  },

  // แก้ไขข้อมูลผู้จำหน่าย
  // BUG-S162-P1: pass last-seen updated_at as `ifMatch` to enable backend optimistic lock (stale→409). Optional.
  async updateSupplier(id: number, supplierData: Partial<SupplierForm>, ifMatch?: string): Promise<Supplier> {
    try {
      const response = await ApiWithLogging.put(`/suppliers/${id}`, supplierData, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
        {
          service: 'supplierService',
          action: 'update_supplier',
          entityType: 'supplier',
          entityId: id,
          details: { updated_fields: Object.keys(supplierData) }
        }
      );
      return response;
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  },

  // ลบผู้จำหน่าย
  async deleteSupplier(id: number): Promise<void> {
    try {
      await ApiWithLogging.delete(`/suppliers/${id}`, {},
        {
          service: 'supplierService',
          action: 'delete_supplier',
          entityType: 'supplier',
          entityId: id
        }
      );
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error;
    }
  },

  // เปลี่ยนสถานะผู้จำหน่าย (active/inactive)
  async toggleSupplierStatus(id: number): Promise<Supplier> {
    try {
      const response = await ApiWithLogging.patch(`/suppliers/${id}/toggle`, {}, {},
        {
          service: 'supplierService',
          action: 'toggle_supplier_status',
          entityType: 'supplier',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error toggling supplier status:', error);
      throw error;
    }
  },

  // ส่งออกข้อมูลผู้จำหน่ายเป็น Excel
  // ส่งออก Excel — ต้องส่งตัวกรองที่หน้าจอใช้อยู่ไปด้วย ไม่งั้นผู้ใช้ค้นหา/กรองแล้วกดส่งออก
  // จะได้ไฟล์ "ทั้งระบบ" โดยไม่มีอะไรเตือน (GAP-EXPORT-FILTER)
  // ชุดพารามิเตอร์ต้องตรงกับที่ list query ส่ง และตรงกับ signature ของ endpoint
  async exportToExcel(params?: { search?: string; is_active?: boolean }): Promise<Blob> {
    const query: Record<string, any> = {};
    if (params?.search) query.search = params.search;
    if (params?.is_active !== undefined) query.is_active = params.is_active;

    const response = await ApiWithLogging.get('/suppliers/export/excel',
      {
        responseType: 'blob',
        params: query
      },
      {
        service: 'supplierService',
        action: 'export_suppliers_excel',
        entityType: 'supplier'
      }
    );
    return response;
  }
};

export default supplierService;
